import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { MerchantAppTopic } from "@redotech/redo-merchant-app-common/events/merchant-app-event-server";
import { MerchantAppEventServerContext } from "@redotech/redo-merchant-app-common/events/merchant-app-event-server-provider";
import {
  KnowledgeSuggestion,
  NotificationType,
} from "@redotech/redo-model/notification";
import {
  createContext,
  Dispatch,
  memo,
  ReactNode,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from "react";

export interface KnowledgeCaptureSuggestions {
  suggestions: KnowledgeSuggestion[];
  hasRelevantKnowledge: boolean;
}

export interface EditedSuggestionValues {
  scenario: string;
  response: string;
  expiresAt: Temporal.PlainDate | undefined;
}

export interface KnowledgeCaptureContextValue {
  isOpen: boolean;
  conversationId: string | undefined;
  suggestions: KnowledgeCaptureSuggestions | undefined;
  hideKnowledgeCapture: () => void;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  // Modal state that persists across open/close
  editedValues: (EditedSuggestionValues | undefined)[];
  setEditedValues: Dispatch<
    SetStateAction<(EditedSuggestionValues | undefined)[]>
  >;
  currentIndex: number;
  setCurrentIndex: Dispatch<SetStateAction<number>>;
  resolvedSuggestionIndices: Set<number>;
  setResolvedSuggestionIndices: Dispatch<SetStateAction<Set<number>>>;
}

export const KnowledgeCaptureContext = createContext<
  KnowledgeCaptureContextValue | undefined
>(undefined);

KnowledgeCaptureContext.displayName = "KnowledgeCaptureContext";

export interface KnowledgeCaptureProviderProps {
  children: ReactNode;
}

export const KnowledgeCaptureProvider = memo(function KnowledgeCaptureProvider({
  children,
}: KnowledgeCaptureProviderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [conversationId, setConversationId] = useState<string | undefined>(
    undefined,
  );
  const [suggestions, setSuggestions] = useState<
    KnowledgeCaptureSuggestions | undefined
  >(undefined);

  // Modal state that persists across open/close
  const [editedValues, setEditedValues] = useState<
    (EditedSuggestionValues | undefined)[]
  >([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [resolvedSuggestionIndices, setResolvedSuggestionIndices] = useState<
    Set<number>
  >(new Set());

  const isOpenRef = useRef(isOpen);
  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  const eventServer = useRequiredContext(MerchantAppEventServerContext);

  const hideKnowledgeCapture = useHandler(() => {
    setIsOpen(false);
    setConversationId(undefined);
    setSuggestions(undefined);
    // Reset modal state for next session
    setEditedValues([]);
    setCurrentIndex(0);
    setResolvedSuggestionIndices(new Set());
  });

  // Subscribe to user notifications for knowledge capture completion
  useEffect(() => {
    const unsubscribe = eventServer.subscribe({
      topic: MerchantAppTopic.USER_NOTIFICATIONS,
      callback: (notification) => {
        if (
          notification.type === NotificationType.KNOWLEDGE_CAPTURE_COMPLETED
        ) {
          // Ignore if toast is already open to avoid losing current suggestions
          if (isOpenRef.current) {
            return;
          }
          if (notification.suggestions?.length > 0) {
            setConversationId(notification.conversationId);
            setSuggestions({
              suggestions: notification.suggestions,
              hasRelevantKnowledge: notification.hasRelevantKnowledge ?? false,
            });
            setIsOpen(true);
          }
        }
      },
    });

    return unsubscribe;
  }, [eventServer]);

  return (
    <KnowledgeCaptureContext.Provider
      value={{
        isOpen,
        conversationId,
        suggestions,
        hideKnowledgeCapture,
        setIsOpen,
        editedValues,
        setEditedValues,
        currentIndex,
        setCurrentIndex,
        resolvedSuggestionIndices,
        setResolvedSuggestionIndices,
      }}
    >
      {children}
    </KnowledgeCaptureContext.Provider>
  );
});
